import { DEFAULT_BOT_PERSONA } from './bot/types';
import { MediumChannel, SubStatusGroup, UnitType } from './enums';
import type { CompanySettings } from './schemas/companySettings';

/** Categorías de medio iniciales. El mapeo real vive en catalog_medium_categories y es administrable. */
export const DEFAULT_MEDIUM_CATEGORIES: ReadonlyArray<{
  code: string;
  name: string;
  channel: MediumChannel;
  isDigital: boolean;
}> = [
  {
    code: 'META',
    name: 'Meta (Facebook / Instagram)',
    channel: MediumChannel.DIGITAL,
    isDigital: true,
  },
  {
    code: 'META_IA',
    name: 'Meta IA (campañas Advantage+)',
    channel: MediumChannel.DIGITAL,
    isDigital: true,
  },
  { code: 'WEB', name: 'Web / Google', channel: MediumChannel.DIGITAL, isDigital: true },
  {
    code: 'PORTALES',
    name: 'Portales inmobiliarios',
    channel: MediumChannel.DIGITAL,
    isDigital: true,
  },
  { code: 'TIKTOK', name: 'TikTok', channel: MediumChannel.DIGITAL, isDigital: true },
  { code: 'MAILING', name: 'Mailing', channel: MediumChannel.DIGITAL, isDigital: true },
  { code: 'WHATSAPP', name: 'WhatsApp', channel: MediumChannel.DIGITAL, isDigital: true },
  { code: 'LINKEDIN', name: 'LinkedIn', channel: MediumChannel.DIGITAL, isDigital: true },
  { code: 'BASE_DATOS', name: 'Base de datos', channel: MediumChannel.DIGITAL, isDigital: false },
  { code: 'REFERIDO', name: 'Referido', channel: MediumChannel.TRADICIONAL, isDigital: false },
  {
    code: 'TRANSITO',
    name: 'Tránsito (walk-in)',
    channel: MediumChannel.TRADICIONAL,
    isDigital: false,
  },
  { code: 'VOLANTEO', name: 'Volanteo', channel: MediumChannel.TRADICIONAL, isDigital: false },
  { code: 'ACTIVACION', name: 'Activación', channel: MediumChannel.TRADICIONAL, isDigital: false },
  {
    code: 'PANEL',
    name: 'Panel publicitario',
    channel: MediumChannel.TRADICIONAL,
    isDigital: false,
  },
  { code: 'EVENTO', name: 'Evento', channel: MediumChannel.TRADICIONAL, isDigital: false },
  { code: 'FERIA', name: 'Feria', channel: MediumChannel.FERIAS, isDigital: false },
  { code: 'CORREDOR', name: 'Corredor', channel: MediumChannel.CORREDOR, isDigital: false },
  { code: 'OTROS', name: 'Otros', channel: MediumChannel.TRADICIONAL, isDigital: false },
];

/** Medios de captación iniciales → categoría (equivale al CSV CONSOLIDADO_MEDIOS_CAPTACION). */
export const DEFAULT_MEDIA: ReadonlyArray<{ name: string; categoryCode: string }> = [
  { name: 'FACEBOOK', categoryCode: 'META' },
  { name: 'INSTAGRAM', categoryCode: 'META' },
  { name: 'FACEBOOK LEAD ADS', categoryCode: 'META' },
  { name: 'META IA', categoryCode: 'META_IA' },
  { name: 'GOOGLE', categoryCode: 'WEB' },
  { name: 'GOOGLE ADS', categoryCode: 'WEB' },
  { name: 'PAGINA WEB', categoryCode: 'WEB' },
  { name: 'LANDING', categoryCode: 'WEB' },
  { name: 'URBANIA', categoryCode: 'PORTALES' },
  { name: 'ADONDEVIVIR', categoryCode: 'PORTALES' },
  { name: 'PROPERATI', categoryCode: 'PORTALES' },
  { name: 'NEXO INMOBILIARIO', categoryCode: 'PORTALES' },
  { name: 'TIKTOK', categoryCode: 'TIKTOK' },
  { name: 'MAILING', categoryCode: 'MAILING' },
  { name: 'WHATSAPP', categoryCode: 'WHATSAPP' },
  { name: 'LINKEDIN', categoryCode: 'LINKEDIN' },
  { name: 'BASE DE DATOS', categoryCode: 'BASE_DATOS' },
  { name: 'REFERIDO', categoryCode: 'REFERIDO' },
  { name: 'TRANSITO', categoryCode: 'TRANSITO' },
  { name: 'VOLANTEO', categoryCode: 'VOLANTEO' },
  { name: 'ACTIVACION', categoryCode: 'ACTIVACION' },
  { name: 'PANEL', categoryCode: 'PANEL' },
  { name: 'EVENTO', categoryCode: 'EVENTO' },
  { name: 'FERIA', categoryCode: 'FERIA' },
  { name: 'CORREDOR', categoryCode: 'CORREDOR' },
  { name: 'OTROS', categoryCode: 'OTROS' },
];

export const DEFAULT_ENTRY_CHANNELS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'SALA_VENTAS', label: 'Sala de ventas' },
  { code: 'LLAMADA', label: 'Llamada' },
  { code: 'FORMULARIO_WEB', label: 'Formulario web' },
  { code: 'WHATSAPP', label: 'WhatsApp' },
  { code: 'MAIL', label: 'Correo' },
  { code: 'REDES', label: 'Redes sociales' },
];

export const DEFAULT_SUB_STATUSES: ReadonlyArray<{
  code: string;
  label: string;
  group: SubStatusGroup;
  sortOrder: number;
}> = [
  {
    code: 'POR_CONTACTAR',
    label: 'Por contactar',
    group: SubStatusGroup.NO_GESTIONADO,
    sortOrder: 1,
  },
  {
    code: 'NO_GESTIONADO',
    label: 'No gestionado',
    group: SubStatusGroup.NO_GESTIONADO,
    sortOrder: 1,
  },
  { code: 'DUPLICADO', label: 'Duplicado', group: SubStatusGroup.DUPLICADO, sortOrder: 2 },
  { code: 'RECONTACTO', label: 'Recontacto', group: SubStatusGroup.DUPLICADO, sortOrder: 2 },
  { code: 'CONTACTADO', label: 'Contactado', group: SubStatusGroup.GESTIONADO, sortOrder: 3 },
  { code: 'VISITA', label: 'Visita', group: SubStatusGroup.GESTIONADO, sortOrder: 3 },
  { code: 'SEPARACION', label: 'Separación', group: SubStatusGroup.GESTIONADO, sortOrder: 3 },
  { code: 'VENTA', label: 'Venta', group: SubStatusGroup.GESTIONADO, sortOrder: 3 },
  { code: 'DEVOLUCION', label: 'Devolución', group: SubStatusGroup.GESTIONADO, sortOrder: 3 },
];

/** Sub-estados que NO cuentan como lead único. */
export const RECONTACT_SUB_STATUSES: readonly string[] = ['RECONTACTO', 'DUPLICADO'];

export const DEFAULT_DROP_REASONS: ReadonlyArray<{
  code: string;
  label: string;
  isTechnical: boolean;
}> = [
  { code: 'DESISTIO', label: 'Cliente desistió', isTechnical: false },
  { code: 'NO_CALIFICA', label: 'No califica crédito', isTechnical: false },
  { code: 'COMPRO_OTRO', label: 'Compró en otro proyecto', isTechnical: false },
  { code: 'CAMBIO_UNIDAD', label: 'Cambio de unidad', isTechnical: false },
  { code: 'ERROR_DATA', label: 'ERROR DATA', isTechnical: true },
  { code: 'ERROR_REFINANCIAMIENTO', label: 'ERROR EN REFINANCIAMIENTO', isTechnical: true },
];

export const DEFAULT_DESIST_REASONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'PRECIO', label: 'Precio fuera de presupuesto' },
  { code: 'UBICACION', label: 'No le gusta la ubicación' },
  { code: 'SIN_INTERES', label: 'Sin interés / solo cotizaba' },
  { code: 'NO_CONTESTA', label: 'No contesta tras varios intentos' },
  { code: 'COMPRO_OTRO', label: 'Compró en otro proyecto' },
  { code: 'NUMERO_INVALIDO', label: 'Número inválido' },
  { code: 'OTRO', label: 'Otro' },
];

export const DEFAULT_NON_EFFECTIVE_REASONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'NO_CONTESTA', label: 'No contesta' },
  { code: 'APAGADO', label: 'Apagado / fuera de servicio' },
  { code: 'BUZON', label: 'Buzón de voz' },
  { code: 'NUMERO_EQUIVOCADO', label: 'Número equivocado' },
  { code: 'CORTO', label: 'Cortó la llamada' },
];

/** Configuración por empresa por defecto (se guarda en companies.settings, validada con companySettingsSchema). */
export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  digitalCategories: [
    'META',
    'META_IA',
    'WEB',
    'PORTALES',
    'TIKTOK',
    'MAILING',
    'WHATSAPP',
    'LINKEDIN',
  ],
  pautaCategories: ['META', 'META_IA', 'WEB'],
  portalCategories: ['PORTALES'],
  countableUnitTypes: [UnitType.CASA, UnitType.DEPARTAMENTO],
  technicalDropReasons: ['ERROR_DATA', 'ERROR_REFINANCIAMIENTO'],
  firstContactSlaMinutes: 15,
  autoFirstTask: true,
  firstTaskType: 'LLAMADA',
  blockDuplicateManualLeads: false,
  businessHours: { start: '09:00', end: '19:00', days: [1, 2, 3, 4, 5, 6] },
  timezone: 'America/Lima',
  currency: 'USD',
  penPerUsd: 3.75,
  requirePriorSeparationForSale: true,
  semaphore: {
    contactabilidad: { green: 0.6, amber: 0.4 },
    citasConcretadas: { green: 0.5, amber: 0.3 },
    visitasSobreCaptaciones: { green: 0.08, amber: 0.05 },
    separacionesSobreVisitas: { green: 0.15, amber: 0.08 },
    ventasSobreCaptaciones: { green: 0.01, amber: 0.005 },
  },
  testEmailPatterns: ['TEST@FB.COM', '@TEST.COM', '@EXAMPLE.COM'],
  metaIntegration: null,
  whatsappIntegration: null,
  bot: {
    maxAttempts: 3,
    attemptWindowHours: 48,
    quietFrom: '22:00',
    quietTo: '08:00',
    resumeAfterMinutes: 30,
    plan: 'ESTANDAR',
    monthlyBudgetUsd: 50,
    onBudgetExceeded: 'BASICO',
    persona: DEFAULT_BOT_PERSONA,
    priceMode: 'DESDE',
    maxTurns: 12,
    appointmentMinutes: 60,
    disclose: true,
    active: false,
    firstContactTemplateId: null,
  },
};

export const ATTENTION_STATE_LABELS: Record<string, string> = {
  POR_CONTACTAR: 'Por contactar',
  EN_CONVERSACION: 'En conversación',
  PRIORIDAD: 'Prioridad',
  SIN_RESPUESTA: 'Sin respuesta',
  DESCARTE_PROPUESTO: 'Descarte propuesto',
};
export const ATTENTION_OWNER_LABELS: Record<string, string> = {
  BOT: 'Bot',
  HUMAN: 'Asesor (bot en pausa)',
};

export const PURCHASE_HORIZON_LABELS: Record<string, string> = {
  INMEDIATO: 'Inmediato',
  MENOS_3_MESES: 'Menos de 3 meses',
  DE_3_A_6_MESES: '3 a 6 meses',
  DE_6_A_12_MESES: '6 a 12 meses',
  MAS_12_MESES: 'Más de 12 meses',
  SIN_DEFINIR: 'Sin definir',
};
export const PURCHASE_PURPOSE_LABELS: Record<string, string> = {
  VIVIENDA: 'Vivienda',
  INVERSION: 'Inversión',
  SIN_DEFINIR: 'Sin definir',
};
export const FINANCING_LABELS: Record<string, string> = {
  CONTADO: 'Contado',
  HIPOTECARIO: 'Hipotecario',
  MIXTO: 'Mixto',
  CREDITO_DIRECTO: 'Crédito directo',
};
export const UNIT_TYPE_LABELS: Record<string, string> = {
  DEPARTAMENTO: 'Departamento',
  CASA: 'Casa',
  LOTE: 'Lote',
  OFICINA: 'Oficina',
  LOCAL: 'Local',
  ESTACIONAMIENTO: 'Estacionamiento',
  DEPOSITO: 'Depósito',
};
/**
 * «departamento, casa o lote»: las etiquetas en minúscula de los tipos de unidad contables de la empresa
 * (`settings.countableUnitTypes`), en el orden del catálogo, para armar mensajes (Lote 5 · H2, P7). Antes el
 * mensaje decía «departamento, casa o lote» aunque el lote no contara por defecto.
 */
export function countableUnitLabels(countable: readonly UnitType[]): string {
  const labels = Object.keys(UNIT_TYPE_LABELS)
    .filter((t) => countable.includes(t as UnitType))
    .map((t) => UNIT_TYPE_LABELS[t]!.toLowerCase());
  if (labels.length === 0) return 'unidad contable';
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} o ${labels[labels.length - 1]}`;
}

/** Mensaje (web y API) cuando una separación llega sin ninguna unidad, con los tipos contables de la empresa. */
export function separationNeedsUnitMessage(countable: readonly UnitType[]): string {
  return `Elige al menos una unidad (${countableUnitLabels(countable)})`;
}

/** Mensaje (web y API) cuando la separación solo trae estacionamientos o depósitos (P7, opción A del dueño). */
export function separationNeedsCountableUnitMessage(countable: readonly UnitType[]): string {
  return `La separación necesita al menos una unidad contable (${countableUnitLabels(countable)}); los estacionamientos y depósitos se agregan además, nunca solos`;
}

export const UNIT_STATUS_LABELS: Record<string, string> = {
  DISPONIBLE: 'Disponible',
  SEPARADO: 'Separado',
  VENDIDO: 'Vendido',
  BLOQUEADO: 'Bloqueado',
};
export const QUOTE_STATUS_LABELS: Record<string, string> = {
  VIGENTE: 'Vigente',
  EXPIRADA: 'Expirada',
  ANULADA: 'Anulada',
  CONVERTIDA: 'Convertida',
};
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrador',
  GERENTE: 'Gerente',
  JEFE_COMERCIAL: 'Jefe comercial',
  COORDINADOR: 'Coordinador',
  ASESOR: 'Asesor',
  MARKETING: 'Marketing',
  LECTURA: 'Solo lectura',
  BOT: 'Bot',
};
/** Vigencia por defecto de una proforma (días). */
export const DEFAULT_QUOTE_VALIDITY_DAYS = 15;

/**
 * Ventana para registrar una gestión o visita con la fecha en que ocurrió (Lote 2 · E3): hasta 30 días atrás,
 * nunca en el futuro. `OCCURRED_AT_FUTURE_TOLERANCE_MS` absorbe el desfase de reloj entre el navegador y el servidor.
 */
export const OCCURRED_AT_MAX_DAYS_BACK = 30;
export const OCCURRED_AT_FUTURE_TOLERANCE_MS = 5 * 60_000;

export const NULL_DATE_SENTINELS = ['1900-01-01'];
export const LIMA_UTC_OFFSET_HOURS = -5;
/** Zona horaria de negocio por defecto (configurable por empresa en `settings.timezone`). */
export const DEFAULT_TIMEZONE = 'America/Lima';
/** Zonas ofrecidas en administración (cualquier zona IANA válida se acepta). */
export const TIMEZONE_OPTIONS = [
  'America/Lima',
  'America/Bogota',
  'America/Guayaquil',
  'America/Santiago',
  'America/Argentina/Buenos_Aires',
  'America/Mexico_City',
  'America/Panama',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'America/New_York',
  'Europe/Madrid',
] as const;

export const API_KEY_SCOPES = ['READ', 'WRITE', 'INGEST'] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];
export const API_KEY_SCOPE_LABELS: Record<ApiKeyScope, string> = {
  READ: 'Lectura (leads, catálogos, conversaciones)',
  WRITE: 'Escritura (gestiones, score, perfil, proformas, consentimiento)',
  INGEST: 'Ingesta (POST /inbound/:source)',
};
/** Vigencia por defecto de una API key (días). */
export const DEFAULT_API_KEY_TTL_DAYS = 365;

/** Presets de importación CSV para los sistemas que usa Performance (columnas de sus exportaciones). */
/**
 * Columnas de la plantilla Excel/CSV de carga masiva (volantes, eventos, bases de datos).
 * Los encabezados se reconocen automáticamente por `suggestMapping`; el orden es el de la plantilla descargable.
 */
/** Máximo de separaciones que devuelve el historial (export Excel); por encima se pide acotar el periodo. */
export const SEPARATIONS_REPORT_MAX_ROWS = 20_000;

/** Tope de filas de las exportaciones de leads y del detalle del embudo (Excel / CSV); por encima se pide acotar. */
export const EXPORT_MAX_ROWS = 5_000;

/**
 * Tope de leads por acción masiva desde la tabla (reasignar y etiquetar; Lote 6 · I4). La web deshabilita la acción
 * por encima con «Máximo 500 por acción; afina el filtro»; el Excel conserva su tope de `EXPORT_MAX_ROWS`.
 */
export const BULK_ACTION_MAX_LEADS = 500;

/**
 * Cuántos leads toma «Repartir» en una llamada (los más antiguos primero; el resto queda en la bolsa y se vuelve a
 * repartir). Lote 6 · I4: el reparto va por tandas de 100 y responde cuántos quedan.
 */
export const DISTRIBUTE_MAX_LEADS = 1_000;

/** Calendario de Mi día (7.25): días como máximo entre «desde» y «hasta», ambos incluidos (≈ 3 meses). */
export const MY_DAY_RANGE_MAX_DAYS = 92;
/** Tareas por sección (vencidas / hoy / próximas) que Mi día lista; los contadores no tienen tope (F3). */
export const MY_DAY_TASK_LIMIT = 200;
/** Leads nuevos sin contactar que Mi día lista (los más antiguos primero); el contador no tiene tope (F3). */
export const MY_DAY_NEW_LEADS_LIMIT = 100;

/**
 * Lote 2 · E1: una visita registrada por error solo se anula dentro de estas horas desde su registro (la fecha de
 * cierre o la de creación, la más reciente: `visitRegisteredAt`); después queda como hecho y se corrige por soporte.
 */
export const ANNUL_VISIT_WINDOW_HOURS = 24;

/**
 * Lote 2 · E1: una visita registrada concreta la cita pendiente cuya hora programada cae entre estos días antes
 * de la visita (regularizaciones tardías; tope fijado por el dueño del producto) y estos días después (visita adelantada).
 */
export const APPOINTMENT_LINK_DAYS_BEFORE = 7;
export const APPOINTMENT_LINK_DAYS_AFTER = 7;

export const IMPORT_TEMPLATE_COLUMNS: ReadonlyArray<{
  header: string;
  field: string;
  required: boolean;
  example: string;
  hint: string;
}> = [
  {
    header: 'Nombres',
    field: 'firstName',
    required: true,
    example: 'Rosa',
    hint: 'Nombres de la persona',
  },
  {
    header: 'Apellido paterno',
    field: 'lastNamePaternal',
    required: false,
    example: 'Huamán',
    hint: '',
  },
  {
    header: 'Apellido materno',
    field: 'lastNameMaternal',
    required: false,
    example: 'Díaz',
    hint: '',
  },
  {
    header: 'Celular',
    field: 'phone',
    required: true,
    example: '987654321',
    hint: 'Solo dígitos; el +51 es opcional. Con celular, correo o DNI se identifica a la persona.',
  },
  { header: 'Correo', field: 'email', required: false, example: 'rosa@example.com', hint: '' },
  { header: 'DNI', field: 'documentNumber', required: false, example: '45678912', hint: '' },
  { header: 'Distrito', field: 'district', required: false, example: 'Jesús María', hint: '' },
  {
    header: 'Proyecto',
    field: 'projectRef',
    required: false,
    example: 'Castilla Park',
    hint: 'Nombre o código del proyecto. Si se deja vacío se usa el proyecto por defecto de la importación.',
  },
  {
    header: 'Asesor',
    field: 'advisorRef',
    required: false,
    example: 'mquispe',
    hint: 'Usuario, correo o nombre del asesor. Vacío = asesor por defecto de la importación o reparto por turnos.',
  },
  {
    header: 'Medio',
    field: 'medium',
    required: false,
    example: 'VOLANTEO',
    hint: 'Medio de captación del catálogo (VOLANTEO, EVENTO, REFERIDO…). Vacío = medio por defecto.',
  },
  {
    header: 'Fecha de captación',
    field: 'capturedAt',
    required: false,
    example: '05/09/2026 16:30',
    hint: 'dd/mm/aaaa [hh:mm]. Vacío = fecha por defecto de la importación o el momento de la carga.',
  },
  {
    header: 'Nota',
    field: 'note',
    required: false,
    example: 'Se acercó al stand del evento',
    hint: '',
  },
];

export const IMPORT_PRESETS: ReadonlyArray<{
  source: string;
  name: string;
  description: string;
  fieldMap: Record<string, string>;
  defaults: Record<string, string>;
}> = [
  {
    source: 'csv',
    name: 'Genérico (sugerido por columnas)',
    description: 'Detecta nombre, celular, correo, proyecto y medio por el nombre de las columnas.',
    fieldMap: {},
    defaults: {},
  },
  {
    source: 'evolta',
    name: 'Evolta — prospectos',
    description:
      'Exportación de prospectos de Evolta: codprospecto como id externo, medio y canal originales, fecha_registro como captación.',
    fieldMap: {
      externalId: 'codprospecto',
      firstName: 'nombres',
      lastNamePaternal: 'apellido_paterno',
      lastNameMaternal: 'apellido_materno',
      phone: 'celular',
      email: 'email',
      documentNumber: 'nro_documento',
      projectRef: 'proyecto',
      medium: 'medio_captacion',
      entryChannel: 'canal_entrada',
      capturedAt: 'fecha_registro',
      note: 'observacion',
      district: 'distrito',
    },
    defaults: {},
  },
  {
    source: 'sperant',
    name: 'Sperant — clientes',
    description:
      'Exportación de clientes de Sperant: id como id externo, fuente como medio, fecha de creación como captación.',
    fieldMap: {
      externalId: 'id',
      firstName: 'first_name',
      lastNamePaternal: 'last_name',
      phone: 'phone',
      email: 'email',
      documentNumber: 'document_number',
      projectRef: 'project',
      medium: 'source',
      entryChannel: 'channel',
      capturedAt: 'created_at',
      utmCampaign: 'campaign',
      note: 'comment',
    },
    defaults: {},
  },
];
